import React, { useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AccessTimeRounded,
  BlockRounded,
  SmsOutlined,
} from "@mui/icons-material";

// Theme-token background for the inline status message, keyed by message type.
const messageSx = (type) => (theme) => {
  const map = {
    ready: theme.palette.info.main,
    cooldown: theme.palette.warning.main,
    blocked: theme.palette.error.main,
  };
  const color = map[type] || theme.palette.info.main;
  return {
    p: "8px 12px",
    borderRadius: 1.5,
    mb: 1,
    bgcolor: alpha(color, 0.1),
    border: `1px solid ${alpha(color, 0.25)}`,
    color: color,
  };
};

const OtpResendButton = ({
  cooldownHook,
  onResend,
  isLoading = false,
  showMessage = true,
  size = "medium",
}) => {
  const {
    canResend,
    isOnCooldown,
    isBlocked,
    formattedTime,
    message,
    remainingTime,
    nextDelay,
  } = cooldownHook;

  const handleResend = () => {
    if (!canResend || isLoading) return;
    if (onResend) onResend();
  };

  const buttonColor = isBlocked ? "error" : "secondary";

  const buttonContent = useCallback(() => {
    if (isLoading) return "Sending...";
    if (isBlocked)
      return (
        <>
          <BlockRounded fontSize="small" sx={{ mr: 0.5 }} />
          Blocked
        </>
      );
    if (isOnCooldown)
      return (
        <>
          <AccessTimeRounded fontSize="small" sx={{ mr: 0.5 }} />
          {formattedTime}
        </>
      );
    return (
      <>
        <SmsOutlined fontSize="small" sx={{ mr: 0.5 }} />
        Resend OTP
      </>
    );
  }, [isLoading, isBlocked, isOnCooldown, formattedTime]);

  const isDisabled = !canResend || isLoading;

  const progressPercent =
    isOnCooldown && nextDelay > 0
      ? Math.max(0, Math.min(100, (remainingTime / nextDelay) * 100))
      : 0;

  return (
    <Box sx={{ width: "100%" }}>
      {showMessage && message && (
        <Box sx={messageSx(message.type)}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {message.title}
          </Typography>
          <Typography sx={{ fontSize: 11, mt: 0.5, opacity: 0.85 }}>
            {message.message}
          </Typography>
        </Box>
      )}

      <Tooltip
        title={
          isBlocked
            ? "Too many attempts. Please wait before trying again."
            : isOnCooldown
            ? `Please wait ${formattedTime} before resending`
            : "Click to send a new verification code"
        }
        arrow
      >
        <span>
          <Button
            fullWidth
            size={size}
            variant="contained"
            color={buttonColor}
            onClick={handleResend}
            disabled={isDisabled}
            sx={{
              minHeight: 36,
              borderRadius: 2,
              position: "relative",
              "&.Mui-disabled": {
                bgcolor: (theme) =>
                  isOnCooldown
                    ? alpha(theme.palette.secondary.main, 0.5)
                    : undefined,
                color: (theme) =>
                  isOnCooldown ? theme.palette.secondary.contrastText : undefined,
              },
            }}
          >
            {buttonContent()}
            {isLoading && (
              <CircularProgress
                size={16}
                sx={{ position: "absolute", right: 8, color: "inherit" }}
              />
            )}
          </Button>
        </span>
      </Tooltip>

      {isOnCooldown && (
        <Box
          sx={{
            mt: 1,
            height: "2px",
            borderRadius: "1px",
            overflow: "hidden",
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.2),
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${progressPercent}%`,
              bgcolor: "warning.main",
              transition: "width 1s linear",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default OtpResendButton;
